import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { TreeViewBox } from '~/components/elements/TreeViewBox'
import {
  QuestionTreeView,
  type QuestionTreeViewProps,
} from '~/components/features/QuestionTreeView'
import { type Question as QuestionBase } from '~/gql/graphql'
import { useLogEvent } from '~/hooks/useLogEvent'
import { compact, exists, QuestionOp } from '~/utils'

type Question = App.PickGraphQL<
  QuestionBase,
  'mt_questions' | 'question_id' | 'question_text'
>

export type QuestionTreeViewBoxInnerProps<TQuestion extends Question> = {
  'data-testid'?: string
  isMatrixQuestionSelectable?: boolean
  onSelectedQuestionIdChange?: (questionIds: TQuestion['question_id'][]) => void
  questions: TQuestion[]
  selectedQuestionIds: TQuestion['question_id'][]
}

export const QuestionTreeViewBoxInner = <TQuestion extends Question>({
  'data-testid': dataTestId,
  isMatrixQuestionSelectable = false,
  onSelectedQuestionIdChange,
  questions,
  selectedQuestionIds,
}: QuestionTreeViewBoxInnerProps<TQuestion>) => {
  const { t } = useTranslation()
  const logEvent = useLogEvent()

  const [filter, setFilter] = React.useState('')

  const treeQuestions = React.useMemo(() => {
    const questionsWithItems = questions.map((question) => {
      if (question.mt_questions.length === 0) {
        return { ...question, items: null }
      }

      const items = compact([
        isMatrixQuestionSelectable && question,
        ...questions.filter(({ question_id }) =>
          question.mt_questions.includes(question_id),
        ),
      ]).map(({ mt_questions: _mt_questions, ...rest }) => rest)

      return { ...question, items }
    })

    const itemQuestionIds = questions.flatMap(R.prop('mt_questions'))

    return R.differenceWith(
      questionsWithItems,
      itemQuestionIds,
      (question, question_id) => question.question_id === question_id,
    )
  }, [isMatrixQuestionSelectable, questions])

  const unselectedTreeQuestions = React.useMemo(
    () =>
      treeQuestions
        .map((treeQuestion) => {
          if (!exists(treeQuestion.items)) {
            return treeQuestion
          }

          return {
            ...treeQuestion,
            items: treeQuestion.items.filter(
              ({ question_id, question_text }) => {
                const hit =
                  question_id.toLowerCase().includes(filter.toLowerCase()) ||
                  question_text.toLowerCase().includes(filter.toLowerCase())
                const isSelected = selectedQuestionIds.includes(question_id)

                return hit && !isSelected
              },
            ),
          }
        })
        .filter((treeQuestion) => {
          if (exists(treeQuestion.items)) {
            return 0 < treeQuestion.items.length
          }

          const { question_id, question_text } = treeQuestion
          const hit =
            question_id.toLowerCase().includes(filter.toLowerCase()) ||
            question_text.toLowerCase().includes(filter.toLowerCase())
          const isSelected = selectedQuestionIds.includes(question_id)

          return hit && !isSelected
        }),
    [filter, selectedQuestionIds, treeQuestions],
  )

  const selectedTreeQuestions = React.useMemo(
    () =>
      treeQuestions
        .map((treeQuestion) => {
          if (!exists(treeQuestion.items)) {
            return treeQuestion
          }

          return {
            ...treeQuestion,
            items: treeQuestion.items.filter(({ question_id }) => {
              const isSelected = selectedQuestionIds.includes(question_id)

              return isSelected
            }),
          }
        })
        .filter((treeQuestion) => {
          if (exists(treeQuestion.items)) {
            return 0 < treeQuestion.items.length
          }

          const { question_id } = treeQuestion
          const isSelected = selectedQuestionIds.includes(question_id)

          return isSelected
        }),
    [selectedQuestionIds, treeQuestions],
  )

  const handleUnselectedTreeViewItemClick = React.useCallback<
    EH<QuestionTreeViewProps<TQuestion>, 'onItemClick'>
  >(
    (questionIds) => {
      const newQuestions = questions.filter(({ question_id }) =>
        [...selectedQuestionIds, ...questionIds].includes(question_id),
      )

      const newQuestionIds = newQuestions.map(({ question_id }) => question_id)
      onSelectedQuestionIdChange?.(newQuestionIds)

      const eventProperties = newQuestions
        .filter(({ question_id }) => questionIds.includes(question_id))
        .map((question) => ({
          key: QuestionOp.toDisplayId(question),
          value: QuestionOp.toText(question),
        }))

      logEvent('Select question', { eventProperties })
    },
    [logEvent, onSelectedQuestionIdChange, questions, selectedQuestionIds],
  )

  const handleSelectedTreeViewItemClick = React.useCallback<
    EH<QuestionTreeViewProps<TQuestion>, 'onItemClick'>
  >(
    (questionIds) => {
      const newQuestionIds = R.difference(selectedQuestionIds, questionIds)
      onSelectedQuestionIdChange?.(newQuestionIds)

      logEvent('Delete question')
    },
    [logEvent, onSelectedQuestionIdChange, selectedQuestionIds],
  )

  return (
    <TreeViewBox
      data-testid={dataTestId}
      search={{ filter, onFilterChange: setFilter }}
      selectedTitle={t('selected_questionnaires')}
      selectedTreeView={
        <QuestionTreeView
          key={selectedTreeQuestions.map((q) => q.question_id).join()}
          defaultExpandedIds={selectedTreeQuestions
            .filter(({ items }) => exists(items))
            .map(({ question_id }) => question_id)}
          treeQuestions={selectedTreeQuestions}
          onItemClick={handleSelectedTreeViewItemClick}
        />
      }
      unselectedTitle={t('unselected_questionnaires')}
      unselectedTreeView={
        <QuestionTreeView
          treeQuestions={unselectedTreeQuestions}
          onItemClick={handleUnselectedTreeViewItemClick}
        />
      }
    />
  )
}
